import React from 'react';
import { Box, Container, Typography, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import Button from '../Button';
import getRedirectUrlAfterLogin from 'utils/getRedirectUrlAfterLogin';

const useStyles = makeStyles((theme) => ({
  section: {
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(10),
    backgroundColor: 'rgba(245, 245, 245, 0.3)',
  },
  container: {
    maxWidth: '1280px',
  },
  header: {
    textAlign: 'center',
    marginBottom: theme.spacing(8),
    animation: '$fadeIn 0.6s ease-out',
  },
  title: {
    fontSize: '1.875rem',
    fontWeight: 700,
    color: '#121212',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      fontSize: '2.25rem',
    },
  },
  subtitle: {
    fontSize: '0.875rem',
    color: '#616161',
    maxWidth: '768px',
    margin: '0 auto',
    [theme.breakpoints.up('md')]: {
      fontSize: '1rem',
    },
  },
  featureCard: {
    backgroundColor: '#FFFFFF',
    padding: theme.spacing(4),
    borderRadius: '12px',
    border: '2px solid #F5F5F5',
    transition: 'all 0.3s ease',
    height: '100%',
    animation: '$fadeInUp 0.8s ease-out',
    '&:hover': {
      borderColor: '#D91A5B',
      boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    },
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    marginBottom: theme.spacing(2),
    color: '#D91A5B',
  },
  iconImage: {
    width: '48px',
    height: '48px',
    marginBottom: theme.spacing(2),
    filter:
      'invert(23%) sepia(89%) saturate(3026%) hue-rotate(320deg) brightness(91%) contrast(93%)',
  },
  featureTitle: {
    fontSize: '1.25rem',
    fontWeight: 700,
    marginBottom: theme.spacing(1.5),
    color: '#121212',
  },
  featureDescription: {
    color: '#616161',
    lineHeight: 1.7,
  },
  ctaContainer: {
    textAlign: 'center',
    marginTop: theme.spacing(6),
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
  '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(30px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },
}));

const features = [
  {
    icon: 'CheckCircle',
    title: 'Smart & Accurate',
    description: '75,000+ scientifically accurate illustrations vetted by domain experts',
  },
  {
    icon: 'FlashOn',
    title: 'Fast & Intuitive',
    description: 'Drag-and-drop editor with no design skills needed – create in minutes',
  },
  {
    icon: 'publication',
    title: 'Publication-Ready',
    description: 'High-resolution exports (1200 DPI) with full publication rights',
  },
];

export default function WhyChoose() {
  const classes = useStyles();

  const handleCreateNowClick = () => {
    window.location.href = getRedirectUrlAfterLogin();
  };

  const renderIcon = (iconType) => {
    if (iconType === 'CheckCircle') {
      return <CheckCircleIcon className={classes.iconWrapper} style={{ fontSize: 48 }} />;
    } else if (iconType === 'FlashOn') {
      return <FlashOnIcon className={classes.iconWrapper} style={{ fontSize: 48 }} />;
    } else {
      return (
        <img
          src="/images/scientific-diagram-maker/publication-icon.png"
          alt=""
          className={classes.iconImage}
        />
      );
    }
  };

  return (
    <section className={classes.section}>
      <Container className={classes.container}>
        <Box className={classes.header}>
          <Typography className={classes.title} component="h2">
            Why Choose Mind The Graph's Science Diagram Maker
          </Typography>
          <Typography className={classes.subtitle}>
            Smart, accurate, and fast — your ticket to effortlessly creating publication-ready
            science diagrams.
          </Typography>
        </Box>

        <Grid container spacing={4} style={{ marginBottom: '48px' }}>
          {features.map((feature, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Box className={classes.featureCard} style={{ animationDelay: `${index * 150}ms` }}>
                {renderIcon(feature.icon)}
                <Typography className={classes.featureTitle}>{feature.title}</Typography>
                <Typography className={classes.featureDescription}>
                  {feature.description}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box className={classes.ctaContainer}>
          <Button size="large" variant="contained" onClick={handleCreateNowClick} component="a">
            Create Now – It's Free
          </Button>
        </Box>
      </Container>
    </section>
  );
}
